import axios from 'axios';
import config from '../config.json';
import { getCurrentIdToken } from './authHelpers';

const MAX_BYTES = 10 * 1024 * 1024;

const MIME_TO_EXT = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/gif': 'gif',
  'image/webp': 'webp',
};

const baseUrl = config.api.baseUrl || 'http://localhost:8000';
const apiV1 = `${baseUrl}/api/v1`;

const IMAGEKIT_UPLOAD_URL = 'https://upload.imagekit.io/api/v1/files/upload';

/**
 * Upload a product image via ImageKit (auth from backend, upload from browser).
 * @param {File} file
 * @returns {Promise<string>} ImageKit CDN URL
 */
export async function uploadProductImage(file) {
  const idToken = await getCurrentIdToken();
  if (!idToken) {
    throw new Error('You must be signed in to upload images.');
  }

  const ext = MIME_TO_EXT[file.type];
  if (!ext) {
    throw new Error('Please choose a JPEG, PNG, GIF, or WebP image.');
  }

  if (file.size > MAX_BYTES) {
    throw new Error('Image must be 10MB or smaller.');
  }

  const { data: auth } = await axios.get(`${apiV1}/media/imagekit-auth`, {
    headers: { Authorization: `Bearer ${idToken}` },
  });

  const rawName =
    typeof file.name === 'string' && file.name.trim()
      ? file.name.trim().replace(/[^\w.-]+/g, '_')
      : `product-${Date.now()}`;
  const fileName = rawName.includes('.') ? rawName : `${rawName}.${ext}`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileName', fileName);
  formData.append('publicKey', auth.publicKey);
  formData.append('signature', auth.signature);
  formData.append('expire', String(auth.expire));
  formData.append('token', auth.token);
  formData.append('useUniqueFileName', 'true');

  const uploadRes = await fetch(IMAGEKIT_UPLOAD_URL, {
    method: 'POST',
    body: formData,
  });

  const body = await uploadRes.json().catch(() => ({}));

  if (!uploadRes.ok) {
    const msg =
      body?.message ||
      body?.help ||
      (typeof body === 'string' ? body : null) ||
      `ImageKit upload failed (${uploadRes.status})`;
    throw new Error(msg);
  }

  if (!body.url) {
    throw new Error('ImageKit did not return a file URL.');
  }

  return body.url;
}

/**
 * Remove a file from ImageKit (admin). Non-ImageKit URLs are skipped on the server.
 * @param {string} imageUrl
 * @returns {Promise<{ ok?: boolean, skipped?: string, deleted?: boolean, alreadyGone?: boolean }>}
 */
export async function deleteProductImageFromImageKit(imageUrl) {
  const idToken = await getCurrentIdToken();
  if (!idToken) {
    throw new Error('You must be signed in to delete images.');
  }
  if (!imageUrl || typeof imageUrl !== 'string' || !imageUrl.trim()) {
    return { ok: true, skipped: 'empty' };
  }

  const { data } = await axios.post(
    `${apiV1}/media/imagekit-delete`,
    { url: imageUrl.trim() },
    { headers: { Authorization: `Bearer ${idToken}` } }
  );
  return data;
}
